import React from 'react';
import MaterialTable, { MTableAction } from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';

const TABLE_HEADER_STYLE = {
  backgroundColor: '#f7f9fc',
  color: '#6b7280',
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  padding: '12px 16px',
  borderTop: '1px solid #edf0f5',
  borderBottom: '1px solid #edf0f5',
  whiteSpace: 'nowrap',
};

const TABLE_CELL_STYLE = {
  padding: '12px 16px',
  fontSize: '0.88rem',
  color: '#1f2937',
};

export default class SupportServicesDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();
    this.searchTimer = null;

    this.columns = [
      {
        title: this.label('image', 'Image'),
        field: 'image_url',
        sorting: false,
        searchable: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 72, padding: '8px 12px' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 72 },
        render: rowData => (
          rowData.image_url
            ? <img src={rowData.image_url} alt="" className="support-services-card__thumb" />
            : <span className="support-services-card__thumb support-services-card__thumb--empty">—</span>
        ),
      },
      {
        title: this.label('name', 'Name'),
        field: 'name',
        orderField: 'name',
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 180 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 180 },
        render: rowData => (
          <a href={rowData.support_service_url} className="ehc-service-orders-card__link">
            {rowData.name}
          </a>
        ),
      },
      {
        title: this.label('title', 'Sub Title'),
        field: 'title',
        orderField: 'title',
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 160 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 160 },
      },
      {
        title: this.label('company', 'Company'),
        field: 'company_name',
        orderField: 'companies.name',
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 140 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 140 },
        render: rowData => (
          rowData.company_url
            ? <a href={rowData.company_url} className="ehc-service-orders-card__link">{rowData.company_name}</a>
            : <span className="support-services-card__empty">—</span>
        ),
      },
      {
        title: this.label('package_type', 'Type'),
        field: 'for_company',
        sorting: false,
        searchable: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 110 },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 110 },
        render: rowData => (
          <span className={`badge ${rowData.for_company ? 'badge-primary' : 'badge-secondary'}`}>
            {rowData.for_company ? this.label('type_company', 'Company') : this.label('type_general', 'General')}
          </span>
        ),
      },
      {
        title: this.label('seens_count', 'Unique Seen'),
        field: 'seens_count',
        type: 'numeric',
        orderField: 'seens_count',
        cellStyle: { ...TABLE_CELL_STYLE, width: 100, textAlign: 'center' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 100, textAlign: 'center' },
      },
      {
        title: this.label('created_at', 'Created At'),
        field: 'created_at',
        orderField: 'created_at',
        searchable: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 130, whiteSpace: 'nowrap' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 130 },
      },
      {
        title: this.label('updated_at', 'Updated At'),
        field: 'updated_at',
        orderField: 'updated_at',
        searchable: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 130, whiteSpace: 'nowrap' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 130 },
      },
    ];

    this.state = {
      packageTypeFilter: '',
      typeCounts: {},
      searchInput: '',
      searchQuery: '',
    };
  }

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  label(key, fallback) {
    return this.props.labels?.[key] || fallback;
  }

  getTheme = () => {
    const isDarkMode = document.documentElement.getAttribute('data-mode-theme') === 'dark';
    return createTheme({
      palette: {
        type: isDarkMode ? 'dark' : 'light',
        primary: { main: '#41924E' },
        secondary: { main: '#2e7d38' },
        background: {
          paper: isDarkMode ? '#2a3441' : '#ffffff',
          default: isDarkMode ? '#1e2530' : '#f7f9fc',
        },
      },
      overrides: {
        MuiTableCell: {
          root: {
            borderBottom: isDarkMode ? '1px solid #3a4757' : '1px solid #f0f2f6',
          },
        },
      },
    });
  };

  fetchData = (query) => new Promise((resolve, reject) => {
    const params = new URLSearchParams({
      page: query.page + 1,
      per_page: query.pageSize,
      q: this.state.searchQuery || '',
      sort_by: query.orderBy?.orderField ?? query.orderBy?.field ?? 'created_at',
      sort_order: query.orderDirection ?? 'desc',
    });

    if (this.state.packageTypeFilter) params.set('package_type', this.state.packageTypeFilter);

    fetch(`/api/support_services?${params.toString()}`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load service packages');
        return res.json();
      })
      .then((data) => {
        this.setState({ typeCounts: data.type_counts || {} });
        resolve({
          data: data.support_services,
          page: data.pagination.page - 1,
          totalCount: data.pagination.count,
        });
      })
      .catch(reject);
  });

  refreshTable = () => {
    if (this.tableRef.current) this.tableRef.current.onQueryChange({ page: 0 });
  };

  handleTypeFilter = (packageType) => {
    this.setState({ packageTypeFilter: packageType }, this.refreshTable);
  };

  handleSearchChange = (event) => {
    const value = event.target.value;
    this.setState({ searchInput: value });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.setState({ searchQuery: value.trim() }, this.refreshTable);
    }, 350);
  };

  clearSearch = () => {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.setState({ searchInput: '', searchQuery: '' }, this.refreshTable);
  };

  renderTypeFilters = () => {
    const { typeCounts, packageTypeFilter } = this.state;
    const total = Object.values(typeCounts).reduce((sum, count) => sum + count, 0);
    const filters = [
      { key: '', label: this.label('all_types', 'All'), count: total },
      { key: 'company', label: this.label('type_company', 'Company'), count: typeCounts.company || 0 },
      { key: 'general', label: this.label('type_general', 'General'), count: typeCounts.general || 0 },
    ];

    return (
      <div className="ehc-service-orders-card__filters" role="group" aria-label={this.label('package_type', 'Type')}>
        {filters.map(({ key, label, count }) => (
          <button
            key={key || 'all'}
            type="button"
            className={`ehc-service-orders-card__filter-pill${packageTypeFilter === key ? ' ehc-service-orders-card__filter-pill--active' : ''}`}
            onClick={() => this.handleTypeFilter(key)}
          >
            {label}
            <span className="ehc-service-orders-card__filter-count">{count}</span>
          </button>
        ))}
      </div>
    );
  };

  renderToolbar = () => (
    <div className="benefit-codes-card__header ehc-service-orders-card__header">
      {this.renderTypeFilters()}
      <div className="benefit-codes-card__actions">
        <div className="benefit-codes-card__search">
          <SearchIcon className="benefit-codes-card__search-icon" fontSize="small" />
          <input
            type="text"
            className="benefit-codes-card__search-input"
            placeholder={this.label('search_placeholder', 'Search service packages...')}
            value={this.state.searchInput}
            onChange={this.handleSearchChange}
          />
          {this.state.searchInput && (
            <button
              type="button"
              className="benefit-codes-card__search-clear"
              onClick={this.clearSearch}
              aria-label={this.label('clear_search', 'Clear search')}
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>
        <button
          type="button"
          className="benefit-codes-card__btn benefit-codes-card__btn--icon"
          onClick={this.refreshTable}
          aria-label={this.label('refresh', 'Refresh Data')}
          title={this.label('refresh', 'Refresh Data')}
        >
          <RefreshIcon fontSize="small" />
        </button>
        {this.props.creatable && this.props.new_url && (
          <a href={this.props.new_url} className="benefit-codes-card__btn benefit-codes-card__btn--primary">
            <AddIcon fontSize="small" />
            <span>{this.label('new', 'New Package')}</span>
          </a>
        )}
      </div>
    </div>
  );

  render() {
    const rowActions = this.props.editable
      ? [{
          icon: () => <EditOutlinedIcon fontSize="small" />,
          tooltip: this.label('edit', 'Edit'),
          onClick: (_, rowData) => {
            if (rowData?.edit_url) window.location.href = rowData.edit_url;
          },
        }]
      : [];

    return (
      <MuiThemeProvider theme={this.getTheme()}>
        <div className="benefit-codes-card ehc-service-orders-card support-services-card">
          {this.renderToolbar()}

          <div className="benefit-codes-card__table-wrap ehc-service-orders-card__table-wrap">
            <MaterialTable
              title=""
              columns={this.columns}
              tableRef={this.tableRef}
              data={this.fetchData}
              actions={rowActions}
              onRowClick={(_, rowData) => {
                if (rowData?.support_service_url) window.location.href = rowData.support_service_url;
              }}
              components={{
                Action: props => (
                  <MTableAction
                    {...props}
                    onClick={(event) => {
                      event.stopPropagation();
                      props.action.onClick(event, props.data);
                    }}
                  />
                ),
              }}
              localization={{
                header: { actions: this.label('actions', 'Actions') },
                body: { emptyDataSourceMessage: this.label('empty', 'No service packages found') },
                pagination: { labelRowsSelect: this.label('rows', 'rows') },
              }}
              options={{
                pageSize: 20,
                pageSizeOptions: [20, 30, 50],
                exportButton: false,
                sorting: true,
                search: false,
                toolbar: false,
                grouping: false,
                emptyRowsWhenPaging: false,
                paginationType: 'normal',
                padding: 'default',
                actionsColumnIndex: -1,
                headerStyle: TABLE_HEADER_STYLE,
                rowStyle: { cursor: 'pointer', fontSize: '0.88rem' },
              }}
              style={{ boxShadow: 'none', background: 'transparent' }}
            />
          </div>
        </div>
      </MuiThemeProvider>
    );
  }
}
